import React from 'react';
import { Settings, Plus, Trash2, X, Languages, Loader2, GripVertical } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';

const FooterTab = ({ settings, updateSetting, activeJobs, startJobPolling, fetchSettings }) => {
  const handleTranslateFooter = async () => {
    try {
      toast.info('Footer çevirisi arka planda başlatıldı...');
      const res = await api.post('/admin/homepage/settings/translate-footer');
      
      if (res.data.job_id) {
        startJobPolling(res.data.job_id, 'footer', () => {
          fetchSettings();
        });
      } else {
        toast.success(res.data.message);
        fetchSettings();
      }
    } catch (error) {
      toast.error('Footer çevirisi başlatılamadı');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Alt Bilgi (Footer) Yönetimi</CardTitle>
            <CardDescription>Sitenin alt kısmındaki bilgileri düzenleyin</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTranslateFooter}
              disabled={activeJobs['footer']}
              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            >
              {activeJobs['footer'] ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Languages className="w-4 h-4 mr-1" />
              )}
              {activeJobs['footer'] ? `%${activeJobs['footer'].progress || 0}` : "Footer'ı Çevir"}
            </Button>
            <Switch
              checked={settings.footer_enabled !== false}
              onCheckedChange={(v) => updateSetting('footer_enabled', v)}
            />
          </div>
        </div>
        {settings.footer_translations && Object.keys(settings.footer_translations).length > 0 && (
          <Badge variant="secondary" className="mt-2 bg-green-100 text-green-700">
            <Languages className="w-3 h-3 mr-1" />
            {Object.keys(settings.footer_translations).length} dile çevrildi
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Newsletter Section */}
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm">Bülten Bölümü</h4>
            <Switch
              checked={settings.footer_newsletter_enabled !== false}
              onCheckedChange={(v) => updateSetting('footer_newsletter_enabled', v)}
            />
          </div>
          {settings.footer_newsletter_enabled !== false && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bülten Başlığı</Label>
                <Input
                  value={settings.footer_newsletter_title || ''}
                  onChange={(e) => updateSetting('footer_newsletter_title', e.target.value)}
                  placeholder="Bültenimize Abone Olun"
                />
              </div>
              <div className="space-y-2">
                <Label>Bülten Açıklaması</Label>
                <Input
                  value={settings.footer_newsletter_desc || ''}
                  onChange={(e) => updateSetting('footer_newsletter_desc', e.target.value)}
                  placeholder="En yeni ürünler ve kampanyalardan haberdar olun"
                />
              </div>
            </div>
          )}
        </div>

        {/* Brand Info */}
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-semibold text-sm">Marka Bilgileri</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Marka Adı</Label>
              <Input
                value={settings.footer_brand_name || ''}
                onChange={(e) => updateSetting('footer_brand_name', e.target.value)}
                placeholder="Turkish E-Market"
              />
            </div>
            <div className="space-y-2">
              <Label>Slogan</Label>
              <Input
                value={settings.footer_tagline || ''}
                onChange={(e) => updateSetting('footer_tagline', e.target.value)}
                placeholder="Türkiye'nin en güvenilir online alışveriş platformu"
              />
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-semibold text-sm">Sosyal Medya Linkleri</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(settings.footer_social_links || []).map((social, index) => (
              <div key={social.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{social.name}</span>
                  <Switch
                    checked={social.enabled !== false}
                    onCheckedChange={(v) => {
                      const updated = [...(settings.footer_social_links || [])];
                      updated[index] = { ...updated[index], enabled: v };
                      updateSetting('footer_social_links', updated);
                    }}
                  />
                </div>
                <Input
                  value={social.url || ''}
                  onChange={(e) => {
                    const updated = [...(settings.footer_social_links || [])];
                    updated[index] = { ...updated[index], url: e.target.value };
                    updateSetting('footer_social_links', updated);
                  }}
                  placeholder="URL"
                  className="text-xs"
                />
              </div>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const newSocial = {
                id: `social_${Date.now()}`,
                name: 'Yeni Link',
                url: '',
                icon: 'Link',
                enabled: true
              };
              updateSetting('footer_social_links', [...(settings.footer_social_links || []), newSocial]);
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Sosyal Link Ekle
          </Button>
        </div>

        {/* App Store Links */}
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm">Uygulama Mağazası Linkleri</h4>
            <Switch
              checked={settings.footer_app_links?.enabled !== false}
              onCheckedChange={(v) => updateSetting('footer_app_links', { ...(settings.footer_app_links || {}), enabled: v })}
            />
          </div>
          {settings.footer_app_links?.enabled !== false && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Apple App Store URL</Label>
                <Input
                  value={settings.footer_app_links?.apple_store || ''}
                  onChange={(e) => updateSetting('footer_app_links', { ...(settings.footer_app_links || {}), apple_store: e.target.value })}
                  placeholder="https://apps.apple.com/..."
                />
              </div>
              <div className="space-y-2">
                <Label>Google Play Store URL</Label>
                <Input
                  value={settings.footer_app_links?.google_play || ''}
                  onChange={(e) => updateSetting('footer_app_links', { ...(settings.footer_app_links || {}), google_play: e.target.value })}
                  placeholder="https://play.google.com/store/apps/..."
                />
                <p className="text-xs text-muted-foreground">Google Play linki pazartesi eklenecek</p>
              </div>
            </div>
          )}
        </div>

        {/* WhatsApp Support */}
        <div className="border rounded-lg p-4 space-y-4 bg-green-50/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#25D366] rounded-full flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <h4 className="font-semibold text-sm">WhatsApp ile Destek</h4>
            </div>
            <Switch
              checked={settings.footer_whatsapp?.enabled !== false}
              onCheckedChange={(v) => updateSetting('footer_whatsapp', { ...(settings.footer_whatsapp || {}), enabled: v })}
            />
          </div>
          {settings.footer_whatsapp?.enabled !== false && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>WhatsApp Numarası</Label>
                <Input
                  value={settings.footer_whatsapp?.phone || ''}
                  onChange={(e) => updateSetting('footer_whatsapp', { ...(settings.footer_whatsapp || {}), phone: e.target.value })}
                  placeholder="+905551234567"
                />
                <p className="text-xs text-muted-foreground">Ülke kodu ile birlikte girin (örn: +90...)</p>
              </div>
              <div className="space-y-2">
                <Label>Buton Metni</Label>
                <Input
                  value={settings.footer_whatsapp?.button_text || ''}
                  onChange={(e) => updateSetting('footer_whatsapp', { ...(settings.footer_whatsapp || {}), button_text: e.target.value })}
                  placeholder="WhatsApp ile Destek"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Varsayılan Mesaj</Label>
                <Input
                  value={settings.footer_whatsapp?.message || ''}
                  onChange={(e) => updateSetting('footer_whatsapp', { ...(settings.footer_whatsapp || {}), message: e.target.value })}
                  placeholder="Merhaba, Turkish E-Market'ten yardım almak istiyorum."
                />
                <p className="text-xs text-muted-foreground">Müşteri WhatsApp&apos;ı açtığında bu mesaj otomatik yazılacak</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Columns */}
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm">Footer Sütunları</h4>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newColumn = {
                  id: `column_${Date.now()}`,
                  title: 'Yeni Sütun',
                  enabled: true,
                  links: []
                };
                updateSetting('footer_columns', [...(settings.footer_columns || []), newColumn]);
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              Sütun Ekle
            </Button>
          </div>
          
          {(settings.footer_columns || []).map((column, colIndex) => (
            <div key={column.id} className="border rounded-lg p-4 space-y-3 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-gray-400" />
                  <Input
                    value={column.title || ''}
                    onChange={(e) => {
                      const updated = [...(settings.footer_columns || [])];
                      updated[colIndex] = { ...updated[colIndex], title: e.target.value };
                      updateSetting('footer_columns', updated);
                    }}
                    className="w-48 font-medium"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={column.enabled !== false}
                    onCheckedChange={(v) => {
                      const updated = [...(settings.footer_columns || [])];
                      updated[colIndex] = { ...updated[colIndex], enabled: v };
                      updateSetting('footer_columns', updated);
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const updated = (settings.footer_columns || []).filter((_, i) => i !== colIndex);
                      updateSetting('footer_columns', updated);
                    }}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>

              {/* Links in column */}
              <div className="space-y-2 pl-6">
                {(column.links || []).map((link, linkIndex) => (
                  <div key={link.id} className="flex items-center gap-2">
                    <Input
                      value={link.text || ''}
                      onChange={(e) => {
                        const updated = [...(settings.footer_columns || [])];
                        updated[colIndex].links[linkIndex] = { ...link, text: e.target.value };
                        updateSetting('footer_columns', updated);
                      }}
                      placeholder="Link Metni"
                      className="flex-1 text-sm"
                    />
                    <Input
                      value={link.url || ''}
                      onChange={(e) => {
                        const updated = [...(settings.footer_columns || [])];
                        updated[colIndex].links[linkIndex] = { ...link, url: e.target.value };
                        updateSetting('footer_columns', updated);
                      }}
                      placeholder="URL"
                      className="flex-1 text-sm"
                    />
                    <Switch
                      checked={link.enabled !== false}
                      onCheckedChange={(v) => {
                        const updated = [...(settings.footer_columns || [])];
                        updated[colIndex].links[linkIndex] = { ...link, enabled: v };
                        updateSetting('footer_columns', updated);
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const updated = [...(settings.footer_columns || [])];
                        updated[colIndex].links = column.links.filter((_, i) => i !== linkIndex);
                        updateSetting('footer_columns', updated);
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const newLink = {
                      id: `link_${Date.now()}`,
                      text: 'Yeni Link',
                      url: '/',
                      enabled: true
                    };
                    const updated = [...(settings.footer_columns || [])];
                    updated[colIndex].links = [...(column.links || []), newLink];
                    updateSetting('footer_columns', updated);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Link Ekle
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Info */}
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm">İletişim Bilgileri</h4>
            <Switch
              checked={settings.footer_contact?.enabled !== false}
              onCheckedChange={(v) => updateSetting('footer_contact', { ...(settings.footer_contact || {}), enabled: v })}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Başlık</Label>
              <Input
                value={settings.footer_contact?.title || ''}
                onChange={(e) => updateSetting('footer_contact', { ...(settings.footer_contact || {}), title: e.target.value })}
                placeholder="İletişim"
              />
            </div>
            <div className="space-y-2">
              <Label>Adres</Label>
              <Input
                value={settings.footer_contact?.address || ''}
                onChange={(e) => updateSetting('footer_contact', { ...(settings.footer_contact || {}), address: e.target.value })}
                placeholder="123 Commerce Street..."
              />
            </div>
            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input
                value={settings.footer_contact?.phone || ''}
                onChange={(e) => updateSetting('footer_contact', { ...(settings.footer_contact || {}), phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div className="space-y-2">
              <Label>E-posta</Label>
              <Input
                value={settings.footer_contact?.email || ''}
                onChange={(e) => updateSetting('footer_contact', { ...(settings.footer_contact || {}), email: e.target.value })}
                placeholder="bilgi@turkishemarket.com"
              />
            </div>
          </div>
        </div>

        {/* Bottom Links & Copyright */}
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-semibold text-sm">Alt Bölüm Linkleri & Telif Hakkı</h4>
          <div className="space-y-3">
            {(settings.footer_bottom_links || []).map((link, index) => (
              <div key={link.id} className="flex items-center gap-2">
                <Input
                  value={link.text || ''}
                  onChange={(e) => {
                    const updated = [...(settings.footer_bottom_links || [])];
                    updated[index] = { ...updated[index], text: e.target.value };
                    updateSetting('footer_bottom_links', updated);
                  }}
                  placeholder="Link Metni"
                  className="flex-1"
                />
                <Input
                  value={link.url || ''}
                  onChange={(e) => {
                    const updated = [...(settings.footer_bottom_links || [])];
                    updated[index] = { ...updated[index], url: e.target.value };
                    updateSetting('footer_bottom_links', updated);
                  }}
                  placeholder="URL"
                  className="flex-1"
                />
                <Switch
                  checked={link.enabled !== false}
                  onCheckedChange={(v) => {
                    const updated = [...(settings.footer_bottom_links || [])];
                    updated[index] = { ...updated[index], enabled: v };
                    updateSetting('footer_bottom_links', updated);
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const updated = (settings.footer_bottom_links || []).filter((_, i) => i !== index);
                    updateSetting('footer_bottom_links', updated);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newLink = { id: `bottom_${Date.now()}`, text: 'Yeni Link', url: '/', enabled: true };
                updateSetting('footer_bottom_links', [...(settings.footer_bottom_links || []), newLink]);
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              Alt Link Ekle
            </Button>
          </div>
          <div className="space-y-2 pt-4 border-t">
            <Label>Telif Hakkı Metni</Label>
            <Input
              value={settings.footer_copyright || ''}
              onChange={(e) => updateSetting('footer_copyright', e.target.value)}
              placeholder="© 2024 Turkish E-Market. Tüm hakları saklıdır."
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FooterTab;
